import React, { useCallback, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import ScreenshotMonitorRoundedIcon from '@mui/icons-material/ScreenshotMonitorRounded';
import ImageSearchRoundedIcon from '@mui/icons-material/ImageSearchRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AddCommentRoundedIcon from '@mui/icons-material/AddCommentRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import * as XLSX from 'xlsx';

import {
  createChatSession,
  deleteChatSession,
  getChatSession,
  getChatSessions,
  saveChatSession,
  sendAiMessage,
  type BackendMessage,
  type ChatSessionSummary,
  type SavedMessage,
} from '../api/chat.service';

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND = '#2F7E8E';
const MAX_HISTORY_MESSAGES = 18;

// ── Web Speech API types ──────────────────────────────────────────────────────
interface ISpeechRecognitionEvent extends Event {
  results: { [i: number]: { [j: number]: { transcript: string } } };
}
interface ISpeechRecognition extends EventTarget {
  lang: string; interimResults: boolean; maxAlternatives: number; continuous: boolean;
  start(): void; stop(): void;
  onresult: ((e: ISpeechRecognitionEvent) => void) | null;
  onerror: ((e: Event) => void) | null;
  onend: ((e: Event) => void) | null;
}
type SpeechRecognitionCtor = new () => ISpeechRecognition;
const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | null => {
  const w = window as unknown as Record<string, unknown>;
  if ('SpeechRecognition' in window) return w['SpeechRecognition'] as SpeechRecognitionCtor;
  if ('webkitSpeechRecognition' in window) return w['webkitSpeechRecognition'] as SpeechRecognitionCtor;
  return null;
};
const hasTTS = () => 'speechSynthesis' in window;

// ── Internal chat message ─────────────────────────────────────────────────────
type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  screenshotUrl?: string;
  exportData?: Record<string, unknown>[] | null;
  timestamp: Date;
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtDate = (s: string) => {
  const d = new Date(s);
  const today = new Date();
  const diff = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diff === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff} days ago`;
  return d.toLocaleDateString([], { month: 'short', day: '2-digit' });
};

const makeSessionId = () => `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const autoTitle = (firstUserMsg: string) => {
  const clean = firstUserMsg.trim().replace(/\s+/g, ' ');
  return clean.length > 46 ? clean.slice(0, 44) + '…' : clean || 'New Chat';
};

const exportToExcel = (data: Record<string, unknown>[], filename = 'report') => {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map((r) => headers.map((h) => r[h] ?? ''));
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

// ── Component ─────────────────────────────────────────────────────────────────
const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'chat' | 'history'>('chat'); // which panel is shown
  const [isExpanded, setIsExpanded] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Typewriter simulation
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const pendingMsgRef = useRef<ChatMessage | null>(null);
  const streamIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Session
  const sessionIdRef = useRef<string>(makeSessionId());
  const sessionCreatedRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Voice
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const speechSupported = Boolean(getSpeechRecognitionCtor());

  // TTS
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const ttsSupported = hasTTS();

  // Screen capture
  const [capturedScreen, setCapturedScreen] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // History
  const [historyList, setHistoryList] = useState<ChatSessionSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen && view === 'chat') { scrollToBottom(); setTimeout(() => inputRef.current?.focus(), 150); }
  }, [isOpen, view, scrollToBottom]);

  useEffect(() => { scrollToBottom(); }, [messages, streamingText, scrollToBottom]);

  useEffect(() => {
    if (!isOpen) {
      window.speechSynthesis?.cancel(); setSpeakingId(null);
      recognitionRef.current?.stop(); setIsListening(false);
    }
  }, [isOpen]);

  // ── Typewriter simulation ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isStreaming || !pendingMsgRef.current) return;
    // Capture snapshot NOW — ref may be mutated before the interval callback fires
    const pendingMsg = pendingMsgRef.current;
    const full = pendingMsg.content;
    let i = 0;
    const charsPerTick = Math.max(2, Math.ceil(full.length / 180));
    streamIntervalRef.current = setInterval(() => {
      i += charsPerTick;
      setStreamingText(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(streamIntervalRef.current!);
        streamIntervalRef.current = null;
        setStreamingText('');
        setIsStreaming(false);
        pendingMsgRef.current = null;
        setMessages((prev) => [...prev, pendingMsg]); // use captured snapshot, not the ref
      }
    }, 16);
    return () => {
      if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
    };
  }, [isStreaming]);

  // ── Create session on backend when first message sent ────────────────────
  const ensureSessionCreated = useCallback(async (firstUserMsg: string) => {
    if (sessionCreatedRef.current) return;
    sessionCreatedRef.current = true;
    try {
      await createChatSession({ session_id: sessionIdRef.current, title: autoTitle(firstUserMsg) });
    } catch { sessionCreatedRef.current = false; }
  }, []);

  // ── Auto-save messages to backend ─────────────────────────────────────────
  const saveToBackend = useCallback(async (msgs: ChatMessage[], titleOverride?: string) => {
    if (msgs.length === 0) return;
    setSaveStatus('saving');
    try {
      const payload: SavedMessage[] = msgs.map((m) => ({
        role: m.role,
        content: m.content,
        has_screenshot: Boolean(m.screenshotUrl),
        timestamp: m.timestamp.toISOString(),
      }));
      const title = titleOverride ?? autoTitle(msgs.find((m) => m.role === 'user')?.content ?? 'Chat');
      await saveChatSession(sessionIdRef.current, { messages: payload, title });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch { setSaveStatus('idle'); }
  }, []);

  // ── TTS ──────────────────────────────────────────────────────────────────
  const speak = useCallback((text: string, msgId: string) => {
    if (!ttsSupported) return;
    window.speechSynthesis.cancel();
    if (speakingId === msgId) { setSpeakingId(null); return; }
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-US';
    utt.onend = () => setSpeakingId(null); utt.onerror = () => setSpeakingId(null);
    setSpeakingId(msgId); window.speechSynthesis.speak(utt);
  }, [speakingId, ttsSupported]);

  const stopSpeaking = () => { window.speechSynthesis?.cancel(); setSpeakingId(null); };

  // ── Voice input ───────────────────────────────────────────────────────────
  const startListening = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = 'en-US'; rec.interimResults = false; rec.maxAlternatives = 1; rec.continuous = false;
    rec.onresult = (e: ISpeechRecognitionEvent) => { setInput(e.results[0][0].transcript); setIsListening(false); };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    rec.start(); recognitionRef.current = rec; setIsListening(true);
  }, []);
  const stopListening = useCallback(() => { recognitionRef.current?.stop(); setIsListening(false); }, []);

  // ── Screen capture ────────────────────────────────────────────────────────
  const captureScreen = useCallback(async () => {
    if (isCapturing) return;
    setIsCapturing(true);
    const panel = panelRef.current;
    if (panel) panel.style.visibility = 'hidden';
    try {
      await new Promise<void>((r) => setTimeout(r, 80));
      const canvas = await html2canvas(document.body, { useCORS: true, allowTaint: true, scale: 0.6, logging: false, backgroundColor: '#ffffff', ignoreElements: (el) => el.id === 'chatbot-fab' });
      setCapturedScreen(canvas.toDataURL('image/jpeg', 0.75));
    } catch { setError('Could not capture screen.'); }
    finally { if (panel) panel.style.visibility = 'visible'; setIsCapturing(false); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [isCapturing]);

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = useCallback(async () => {
    const text = input.trim();
    const hasScreen = Boolean(capturedScreen);
    if (!text && !hasScreen) return;
    if (loading || isStreaming) return;

    const displayText = text || 'What do you see on this screen?';
    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: displayText, screenshotUrl: capturedScreen ?? undefined, timestamp: new Date() };
    const nextMessages = [...messages, userMsg];

    setMessages(nextMessages);
    setInput(''); setCapturedScreen(null); setError(null); setLoading(true);

    // Create session on first message
    await ensureSessionCreated(displayText);

    try {
      const sliced = nextMessages.slice(-MAX_HISTORY_MESSAGES);
      const payload: BackendMessage[] = sliced.map((m) => ({
        role: m.role,
        content: m.content,
        ...(m.screenshotUrl ? { screenshotUrl: m.screenshotUrl } : {}),
      }));

      const res = await sendAiMessage(payload);
      const { content: reply, exportData } = res.data;

      const assistantMsg: ChatMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: reply,
        exportData: exportData ?? null,
        timestamp: new Date(),
      };

      // Kick off typewriter — message added to history when animation completes
      pendingMsgRef.current = assistantMsg;
      setLoading(false);
      setIsStreaming(true);

      // Save happens after streaming finishes (watch messages length change)
      const finalMessages = [...nextMessages, assistantMsg];
      void saveToBackend(finalMessages, autoTitle(displayText));

      if (autoSpeak && ttsSupported) {
        const utt = new SpeechSynthesisUtterance(reply.replace(/\[EXPORT_AVAILABLE\]/g, ''));
        utt.lang = 'en-US'; utt.onend = () => setSpeakingId(null); utt.onerror = () => setSpeakingId(null);
        setSpeakingId(assistantMsg.id); window.speechSynthesis.cancel(); window.speechSynthesis.speak(utt);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally { setLoading(false); }
  }, [autoSpeak, capturedScreen, ensureSessionCreated, input, isStreaming, loading, messages, saveToBackend, ttsSupported]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendMessage(); }
  };

  // ── Manual save ───────────────────────────────────────────────────────────
  const handleManualSave = async () => {
    if (messages.length === 0) return;
    await ensureSessionCreated(messages.find((m) => m.role === 'user')?.content ?? 'Chat');
    await saveToBackend(messages);
  };

  // ── New chat ──────────────────────────────────────────────────────────────
  const startNewChat = () => {
    stopSpeaking();
    setMessages([]); setInput(''); setCapturedScreen(null); setError(null);
    sessionIdRef.current = makeSessionId();
    sessionCreatedRef.current = false;
    setSaveStatus('idle');
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 150);
  };

  // ── Load history ──────────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await getChatSessions({ limit: 50 });
      setHistoryList(res.data?.sessions ?? []);
    } catch { setHistoryList([]); }
    finally { setHistoryLoading(false); }
  }, []);

  const openHistory = () => { setView('history'); void loadHistory(); };

  const loadSession = async (session_id: string) => {
    try {
      const res = await getChatSession(session_id);
      const full = res.data;
      if (!full) return;
      const loaded: ChatMessage[] = full.messages.map((m, i) => ({
        id: `loaded-${i}-${Date.now()}`,
        role: m.role,
        content: m.content,
        timestamp: new Date(m.timestamp),
      }));
      setMessages(loaded);
      sessionIdRef.current = full.session_id;
      sessionCreatedRef.current = true;
      setSaveStatus('idle');
      setView('chat');
    } catch { setError('Failed to load session.'); }
  };

  const handleDeleteSession = async (session_id: string) => {
    setDeletingId(session_id);
    try {
      await deleteChatSession(session_id);
      setHistoryList((prev) => prev.filter((s) => s.session_id !== session_id));
    } catch { /* silent */ }
    finally { setDeletingId(null); }
  };

  const stopStreaming = () => {
    if (streamIntervalRef.current) { clearInterval(streamIntervalRef.current); streamIntervalRef.current = null; }
    const pending = pendingMsgRef.current;
    pendingMsgRef.current = null;
    if (pending) setMessages((prev) => [...prev, pending]);
    setIsStreaming(false); setStreamingText('');
  };

  const clearChat = () => {
    stopSpeaking(); stopStreaming(); setMessages([]); setCapturedScreen(null); setError(null);
    sessionIdRef.current = makeSessionId(); sessionCreatedRef.current = false; setSaveStatus('idle');
    inputRef.current?.focus();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* FAB */}
      <Tooltip title={isOpen ? '' : 'AI Assistant'} placement="left" arrow>
        <Box id="chatbot-fab" onClick={() => setIsOpen((p) => !p)}
          sx={{
            position: 'fixed', bottom: 28, right: 28, zIndex: 1500, width: 54, height: 54,
            borderRadius: '50%', cursor: 'pointer',
            background: isOpen ? '#e8f4f7' : `linear-gradient(135deg, #3a7d8c 0%, ${BRAND} 55%, #72bfcc 100%)`,
            border: isOpen ? `2px solid ${BRAND}` : 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isOpen ? '0 2px 12px rgba(76,155,171,0.25)' : '0 6px 24px rgba(76,155,171,0.50)',
            transition: 'all 200ms cubic-bezier(0.4,0,0.2,1)',
            '&:hover': { transform: 'scale(1.07)', boxShadow: '0 8px 28px rgba(76,155,171,0.55)' },
          }}>
          <SmartToyRoundedIcon sx={{ color: isOpen ? BRAND : '#fff', fontSize: 26, transition: 'color 200ms' }} />
        </Box>
      </Tooltip>

      {/* Panel */}
      <Fade in={isOpen} unmountOnExit timeout={220}>
        <Paper ref={panelRef} elevation={0} sx={{
          position: 'fixed',
          bottom: isExpanded ? 0 : 94,
          right: isExpanded ? 0 : 28,
          zIndex: 1500,
          width: isExpanded ? '75vw' : 375,
          height: isExpanded ? '100vh' : 510,
          borderRadius: isExpanded ? '18px 0 0 0' : '18px',
          border: '1.5px solid #d0e9ee',
          boxShadow: '0 20px 60px rgba(40,100,115,0.22), 0 4px 16px rgba(76,155,171,0.14)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff',
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}>

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <Box sx={{ px: 1.75, py: 1.1, flexShrink: 0, background: `linear-gradient(90deg, #2e6e7e 0%, ${BRAND} 55%, #5fb0be 100%)`, display: 'flex', alignItems: 'center', gap: 1 }}>
            {view === 'history' ? (
              <IconButton size="small" onClick={() => setView('chat')} sx={{ color: 'rgba(255,255,255,0.85)', p: 0.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}>
                <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            ) : (
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <SmartToyRoundedIcon sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.4}>
                <Typography fontWeight={700} fontSize={13} sx={{ color: '#fff', lineHeight: 1.25 }}>
                  {view === 'history' ? 'Chat History' : 'AI Assistant'}
                </Typography>
                {view === 'chat' && <AutoAwesomeRoundedIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }} />}
              </Stack>
              <Typography fontSize={10.5} sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.2 }}>
                {view === 'history' ? 'Your saved conversations' : 'GPT-4o Mini · DB · Vision · Voice'}
              </Typography>
            </Box>

            {view === 'chat' && (
              <>
                {/* Save */}
                <Tooltip title={saveStatus === 'saved' ? 'Saved!' : 'Save chat'} arrow>
                  <span>
                    <IconButton size="small" onClick={() => void handleManualSave()} disabled={messages.length === 0 || saveStatus === 'saving'}
                      sx={{ color: saveStatus === 'saved' ? '#a8ffd4' : 'rgba(255,255,255,0.75)', p: 0.5, borderRadius: '8px', backgroundColor: saveStatus === 'saving' ? 'rgba(255,255,255,0.12)' : 'transparent', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                      {saveStatus === 'saving' ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : saveStatus === 'saved' ? <CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> : <SaveRoundedIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
                {/* History */}
                <Tooltip title="Chat history" arrow>
                  <IconButton size="small" onClick={openHistory} sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                    <HistoryRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {/* New chat */}
                <Tooltip title="New chat" arrow>
                  <IconButton size="small" onClick={startNewChat} sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                    <AddCommentRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {/* Auto-speak */}
                {ttsSupported && (
                  <Tooltip title={autoSpeak ? 'Auto-read ON' : 'Auto-read OFF'} arrow>
                    <IconButton size="small" onClick={() => { if (autoSpeak) stopSpeaking(); setAutoSpeak((p) => !p); }}
                      sx={{ color: autoSpeak ? '#fff' : 'rgba(255,255,255,0.5)', p: 0.5, borderRadius: '8px', backgroundColor: autoSpeak ? 'rgba(255,255,255,0.18)' : 'transparent', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                      {autoSpeak ? <RecordVoiceOverRoundedIcon sx={{ fontSize: 16 }} /> : <VolumeOffRoundedIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Tooltip>
                )}
                {/* Clear */}
                {messages.length > 0 && (
                  <Tooltip title="Clear chat" arrow>
                    <IconButton size="small" onClick={clearChat} sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                      <DeleteSweepRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            <Tooltip title={isExpanded ? 'Collapse' : 'Expand (3/4 screen)'} arrow>
              <IconButton size="small" onClick={() => setIsExpanded((p) => !p)} sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                {isExpanded ? <CloseFullscreenRoundedIcon sx={{ fontSize: 15 }} /> : <OpenInFullRoundedIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'rgba(255,255,255,0.75)', p: 0.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* ── History view ─────────────────────────────────────────────────── */}
          {view === 'history' && (
            <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: '#f7fbfc', scrollbarWidth: 'thin', scrollbarColor: '#c5dfe6 #eef6f8', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#c5dfe6', borderRadius: 4 } }}>
              {/* New chat button */}
              <Box sx={{ px: 1.5, pt: 1.5, pb: 0.75 }}>
                <Box onClick={startNewChat} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: 2, border: `1.5px dashed ${BRAND}`, cursor: 'pointer', backgroundColor: '#eef8fb', '&:hover': { backgroundColor: '#dff1f5' } }}>
                  <AddCommentRoundedIcon sx={{ color: BRAND, fontSize: 18 }} />
                  <Typography fontWeight={700} fontSize={13} sx={{ color: BRAND }}>Start New Chat</Typography>
                </Box>
              </Box>

              <Divider sx={{ mx: 1.5, my: 0.75, borderColor: '#d8edf1' }} />

              {historyLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} sx={{ color: BRAND }} />
                  <Typography fontSize={12} sx={{ color: '#8ab5bf', mt: 1 }}>Loading history…</Typography>
                </Stack>
              ) : historyList.length === 0 ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 5, px: 2 }}>
                  <ChatBubbleOutlineRoundedIcon sx={{ color: '#c5dfe6', fontSize: 42 }} />
                  <Typography fontSize={13} sx={{ color: '#8ab5bf' }}>No saved chats yet</Typography>
                  <Typography fontSize={11.5} align="center" sx={{ color: '#b0c8ce' }}>Your conversations will appear here after saving.</Typography>
                </Stack>
              ) : (
                <Stack spacing={0} sx={{ px: 1.5, pb: 1.5 }}>
                  {historyList.map((s) => (
                    <Box key={s.session_id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 1, borderRadius: 2, cursor: 'pointer', transition: 'background 150ms', '&:hover': { backgroundColor: '#e8f5f8' }, '&:hover .del-btn': { opacity: 1 } }}
                      onClick={() => void loadSession(s.session_id)}
                    >
                      <ChatBubbleOutlineRoundedIcon sx={{ color: BRAND, fontSize: 16, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontSize={12.5} fontWeight={600} noWrap sx={{ color: '#1c3e48' }}>{s.title}</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography fontSize={10.5} sx={{ color: '#8ab5bf' }}>{s.message_count} messages</Typography>
                          <Typography fontSize={10.5} sx={{ color: '#b0c8ce' }}>·</Typography>
                          <Typography fontSize={10.5} sx={{ color: '#8ab5bf' }}>{fmtDate(s.updatedAt)}</Typography>
                        </Stack>
                      </Box>
                      <IconButton size="small" className="del-btn"
                        onClick={(e) => { e.stopPropagation(); void handleDeleteSession(s.session_id); }}
                        disabled={deletingId === s.session_id}
                        sx={{ opacity: 0, transition: 'opacity 150ms', p: 0.4, color: '#e57373', '&:hover': { backgroundColor: '#fdecea' } }}>
                        {deletingId === s.session_id ? <CircularProgress size={12} sx={{ color: '#e57373' }} /> : <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />}
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* ── Chat view ────────────────────────────────────────────────────── */}
          {view === 'chat' && (
            <>
              {/* Messages */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, backgroundColor: '#f7fbfc', scrollbarWidth: 'thin', scrollbarColor: '#c5dfe6 #eef6f8', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-track': { backgroundColor: '#eef6f8' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#c5dfe6', borderRadius: 4 } }}>

                {messages.length === 0 && !loading && (
                  <Stack alignItems="center" spacing={1.5} sx={{ mt: 2, px: 2 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #d6eef3 0%, #b8dfe8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <SmartToyRoundedIcon sx={{ color: BRAND, fontSize: 26 }} />
                    </Box>
                    <Typography fontSize={13} fontWeight={600} align="center" sx={{ color: '#5a8f9a' }}>Hi! I'm your medical billing assistant.</Typography>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" justifyContent="center">
                      {[
                        { icon: <MicRoundedIcon sx={{ fontSize: 11 }} />, label: 'Voice' },
                        { icon: <VolumeUpRoundedIcon sx={{ fontSize: 11 }} />, label: 'Read Aloud' },
                        { icon: <ScreenshotMonitorRoundedIcon sx={{ fontSize: 11 }} />, label: 'Read Screen' },
                        { icon: <SaveRoundedIcon sx={{ fontSize: 11 }} />, label: 'Save Chats' },
                      ].map((f) => (
                        <Stack key={f.label} direction="row" alignItems="center" spacing={0.3} sx={{ px: 0.9, py: 0.35, borderRadius: 10, backgroundColor: '#e3f3f6', border: '1px solid #c5e6ed' }}>
                          <Box sx={{ color: BRAND }}>{f.icon}</Box>
                          <Typography sx={{ fontSize: 10, color: BRAND, fontWeight: 600 }}>{f.label}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <Stack spacing={0.65} sx={{ width: '100%', mt: 0.25 }}>
                      {[
                        'Show all users report with department and daily target',
                        'How many open tickets are there by department?',
                        'Who completed their daily target this week?',
                        'Read this screen and explain',
                      ].map((p) => (
                        <Box key={p} onClick={() => { if (p === 'Read this screen and explain') { void captureScreen(); return; } setInput(p); setTimeout(() => inputRef.current?.focus(), 50); }}
                          sx={{ px: 1.4, py: 0.65, borderRadius: 2, border: '1px solid #cde8ee', backgroundColor: '#fff', cursor: 'pointer', fontSize: 12, color: BRAND, fontWeight: 500, transition: 'background 150ms', '&:hover': { backgroundColor: '#e8f5f8' } }}>
                          {p}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                )}

                {messages.map((msg) => (
                  <Stack key={msg.id} direction={msg.role === 'user' ? 'row-reverse' : 'row'} alignItems="flex-end" spacing={0.75}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: msg.role === 'user' ? '#ddf0f4' : BRAND, flexShrink: 0, mb: '2px' }}>
                      {msg.role === 'user' ? <PersonRoundedIcon sx={{ color: BRAND, fontSize: 13 }} /> : <SmartToyRoundedIcon sx={{ color: '#fff', fontSize: 13 }} />}
                    </Avatar>
                    <Box sx={{ maxWidth: '78%' }}>
                      {msg.screenshotUrl && (
                        <Box sx={{ mb: 0.5, borderRadius: '10px 10px 0 0', overflow: 'hidden', border: '1px solid rgba(76,155,171,0.25)' }}>
                          <img src={msg.screenshotUrl} alt="screen" style={{ width: '100%', display: 'block', maxHeight: 100, objectFit: 'cover' }} />
                        </Box>
                      )}
                      <Box sx={{ px: 1.4, py: 0.85, borderRadius: msg.screenshotUrl ? (msg.role === 'user' ? '0 0 3px 14px' : '0 0 14px 3px') : (msg.role === 'user' ? '14px 14px 3px 14px' : '14px 14px 14px 3px'), backgroundColor: msg.role === 'user' ? BRAND : '#ffffff', border: msg.role === 'user' ? 'none' : '1px solid #d0e9ee', boxShadow: msg.role === 'user' ? '0 2px 8px rgba(76,155,171,0.28)' : '0 1px 4px rgba(76,155,171,0.08)' }}>
                        <Typography sx={{ color: msg.role === 'user' ? '#fff' : '#1c3e48', fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.content.replace(/\[EXPORT_AVAILABLE\]/g, '')}
                        </Typography>
                        {msg.role === 'assistant' && msg.exportData && msg.exportData.length > 0 && (
                          <Box
                            onClick={() => exportToExcel(msg.exportData!, autoTitle(messages.find((m) => m.role === 'user')?.content ?? 'report'))}
                            sx={{ mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.1, py: 0.45, borderRadius: '8px', backgroundColor: '#e8f7eb', border: '1px solid #a8ddb5', cursor: 'pointer', '&:hover': { backgroundColor: '#d4f0da' } }}
                          >
                            <TableChartRoundedIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#2e7d32' }}>Export as Excel</Typography>
                            <DownloadRoundedIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
                          </Box>
                        )}
                      </Box>
                      <Stack direction={msg.role === 'user' ? 'row-reverse' : 'row'} alignItems="center" spacing={0.4} sx={{ mt: 0.25, px: 0.5 }}>
                        <Typography sx={{ color: '#b0c8ce', fontSize: 10 }}>{fmtTime(msg.timestamp)}</Typography>
                        {msg.role === 'assistant' && ttsSupported && (
                          <Tooltip title={speakingId === msg.id ? 'Stop' : 'Read aloud'} arrow>
                            <IconButton size="small" onClick={() => speakingId === msg.id ? stopSpeaking() : speak(msg.content, msg.id)} sx={{ p: 0.2, color: speakingId === msg.id ? BRAND : '#c0d8de', '&:hover': { color: BRAND } }}>
                              {speakingId === msg.id ? <StopCircleRoundedIcon sx={{ fontSize: 13 }} /> : <VolumeUpRoundedIcon sx={{ fontSize: 13 }} />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                ))}

                {/* Waiting for backend response */}
                {loading && (
                  <Stack direction="row" alignItems="flex-end" spacing={0.75}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: BRAND, flexShrink: 0 }}>
                      <SmartToyRoundedIcon sx={{ color: '#fff', fontSize: 13 }} />
                    </Avatar>
                    <Box sx={{ px: 1.4, py: 0.9, borderRadius: '14px 14px 14px 3px', backgroundColor: '#fff', border: '1px solid #d0e9ee', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {[0, 1, 2].map((i) => (
                        <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: BRAND, animation: 'db 1.2s infinite ease-in-out', animationDelay: `${i * 0.2}s`, '@keyframes db': { '0%,80%,100%': { transform: 'scale(0.65)', opacity: 0.4 }, '40%': { transform: 'scale(1)', opacity: 1 } } }} />
                      ))}
                    </Box>
                  </Stack>
                )}

                {/* Typewriter streaming bubble */}
                {isStreaming && streamingText !== undefined && (
                  <Stack direction="row" alignItems="flex-end" spacing={0.75}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: BRAND, flexShrink: 0, mb: '2px' }}>
                      <SmartToyRoundedIcon sx={{ color: '#fff', fontSize: 13 }} />
                    </Avatar>
                    <Box sx={{ maxWidth: '78%' }}>
                      <Box sx={{ px: 1.4, py: 0.85, borderRadius: '14px 14px 14px 3px', backgroundColor: '#ffffff', border: '1px solid #d0e9ee', boxShadow: '0 1px 4px rgba(76,155,171,0.08)' }}>
                        <Typography sx={{ color: '#1c3e48', fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {streamingText.replace(/\[EXPORT_AVAILABLE\]/g, '')}
                          <Box component="span" sx={{ display: 'inline-block', width: '2px', height: '14px', backgroundColor: BRAND, ml: '2px', verticalAlign: 'text-bottom', animation: 'blink 0.8s step-end infinite', '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } } }} />
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                )}

                {error && (
                  <Box sx={{ mx: 0.5, px: 1.4, py: 0.85, borderRadius: 2, backgroundColor: '#fff5f6', border: '1px solid #ffd6da' }}>
                    <Typography sx={{ color: '#c0392b', fontSize: 11.5 }}>⚠ {error}</Typography>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* Screenshot preview */}
              {capturedScreen && (
                <Box sx={{ px: 1.5, pt: 0.85, pb: 0, backgroundColor: '#fff', flexShrink: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <img src={capturedScreen} alt="screen" style={{ width: 64, height: 40, objectFit: 'cover', borderRadius: 7, border: `1.5px solid ${BRAND}`, display: 'block' }} />
                      <Box onClick={() => setCapturedScreen(null)} sx={{ position: 'absolute', top: -5, right: -5, width: 14, height: 14, borderRadius: '50%', backgroundColor: '#e74c3c', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <CloseRoundedIcon sx={{ fontSize: 9, color: '#fff' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ImageSearchRoundedIcon sx={{ color: BRAND, fontSize: 13 }} />
                        <Typography fontSize={11.5} fontWeight={700} sx={{ color: BRAND }}>Screen captured</Typography>
                      </Stack>
                      <Typography fontSize={10.5} sx={{ color: '#8ab5bf' }}>Type a question or press Enter</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ mt: 0.85, borderColor: '#e8f3f6' }} />
                </Box>
              )}

              <Divider sx={{ borderColor: '#d8edf1' }} />

              {/* Input */}
              <Box sx={{ px: 1.5, py: 1, backgroundColor: '#fff', flexShrink: 0 }}>
                {isListening && (
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.65 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#e74c3c', animation: 'mp 1s infinite', '@keyframes mp': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.4, transform: 'scale(1.5)' } } }} />
                    <Typography fontSize={11} sx={{ color: '#e74c3c', fontWeight: 600 }}>Listening…</Typography>
                  </Stack>
                )}
                <TextField inputRef={inputRef} fullWidth multiline maxRows={3} size="small" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder={isListening ? 'Listening…' : capturedScreen ? 'Ask about the screen…' : 'Ask anything about medical billing...'}
                  disabled={loading} variant="outlined"
                  InputProps={{
                    sx: { borderRadius: '12px', fontSize: 13, pr: 0.5, backgroundColor: isListening ? '#fff5f5' : capturedScreen ? '#f0fbf5' : '#f4fafc', transition: 'background 200ms', '& fieldset': { borderColor: isListening ? '#e74c3c' : capturedScreen ? '#4caf50' : '#cde8ee' }, '&:hover fieldset': { borderColor: isListening ? '#e74c3c' : capturedScreen ? '#43a047' : BRAND }, '&.Mui-focused fieldset': { borderColor: isListening ? '#e74c3c' : capturedScreen ? '#43a047' : BRAND, borderWidth: '1.5px' } },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack direction="row" spacing={0.2} alignItems="center">
                          <Tooltip title={isCapturing ? 'Capturing…' : 'Read screen'} arrow>
                            <span>
                              <IconButton size="small" onClick={() => void captureScreen()} disabled={loading || isCapturing} sx={{ width: 28, height: 28, borderRadius: '8px', backgroundColor: capturedScreen ? '#e8f7eb' : 'transparent', color: capturedScreen ? '#4caf50' : '#b8d4da', '&:hover': { backgroundColor: '#e8f5f8', color: BRAND } }}>
                                {isCapturing ? <CircularProgress size={12} sx={{ color: BRAND }} /> : <ScreenshotMonitorRoundedIcon sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {speechSupported && (
                            <Tooltip title={isListening ? 'Stop' : 'Speak'} arrow>
                              <span>
                                <IconButton size="small" onClick={isListening ? stopListening : startListening} disabled={loading || isStreaming} sx={{ width: 28, height: 28, borderRadius: '8px', backgroundColor: isListening ? '#fdecea' : 'transparent', color: isListening ? '#e74c3c' : '#b8d4da', '&:hover': { backgroundColor: '#e8f5f8', color: BRAND } }}>
                                  {isListening ? <MicOffRoundedIcon sx={{ fontSize: 14 }} /> : <MicRoundedIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Send" arrow>
                            <span>
                              <IconButton size="small" disabled={(!input.trim() && !capturedScreen) || loading || isStreaming} onClick={() => void sendMessage()} sx={{ width: 28, height: 28, borderRadius: '8px', backgroundColor: (input.trim() || capturedScreen) && !loading && !isStreaming ? BRAND : 'transparent', color: (input.trim() || capturedScreen) && !loading && !isStreaming ? '#fff' : '#b8d4da', transition: 'all 150ms', '&.Mui-disabled': { backgroundColor: 'transparent' } }}>
                                {loading ? <CircularProgress size={12} sx={{ color: BRAND }} /> : <SendRoundedIcon sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </InputAdornment>
                    ),
                  }}
                />
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.35, px: 0.25 }}>
                  <Typography sx={{ color: '#c0d8dd', fontSize: 10 }}>
                    {capturedScreen ? '🖼 Screen ready' : '📷 Monitor icon = read screen'}
                  </Typography>
                  <Typography sx={{ color: '#c0d8dd', fontSize: 10 }}>Enter · Shift+Enter</Typography>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Fade>
    </>
  );
};

export default ChatBot;
